import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatError } from "../utils/api";
import Navbar from "../components/Navbar";
import { Shield, UserPlus, Trash2, Key, Check, X, AlertCircle, CheckCircle, Copy } from "lucide-react";

function Badge({ children, tone = "sage" }) {
  const map = {
    sage: "bg-sage-100 text-sage-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    green: "bg-green-100 text-green-700",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[tone]}`}>{children}</span>;
}

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "consultant" });
  const [resetResult, setResetResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3500);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      setForm({ email: "", name: "", password: "", role: "consultant" });
      setShowCreate(false);
      flash("success", "User created");
      load();
    } catch (e2) {
      flash("error", formatError(e2));
    }
  };

  const handleRoleChange = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.id}`, { role });
      flash("success", `Role updated: ${u.email}`);
      load();
    } catch (e) {
      flash("error", formatError(e));
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      flash("success", `${u.email} ${u.is_active ? "deactivated" : "reactivated"}`);
      load();
    } catch (e) {
      flash("error", formatError(e));
    }
  };

  const handleResetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A temporary password will be shown once.`)) return;
    try {
      const res = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetResult(res.data);
    } catch (e) {
      flash("error", formatError(e));
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      flash("success", "User deleted");
      load();
    } catch (e) {
      flash("error", formatError(e));
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); flash("success", "Copied to clipboard"); };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Administration</p>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-sage-600" />
              <h1 className="font-heading text-2xl font-semibold text-sage-900">User Management</h1>
            </div>
            <p className="text-sage-500 text-sm mt-1">Create, modify roles, deactivate and reset passwords for TheKaizenCabinet users</p>
          </div>
          <button
            data-testid="admin-create-user-button"
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-2 bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" /> {showCreate ? "Cancel" : "New User"}
          </button>
        </div>

        {msg && (
          <div className={`mb-4 flex items-center gap-2 text-sm rounded-md px-3 py-2 ${msg.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
            {msg.type === "success" ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {msg.text}
          </div>
        )}

        {resetResult && (
          <div className="mb-4 bg-amber-50 border border-amber-300 rounded-md p-4" data-testid="temp-password-display">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="font-heading text-sm font-semibold text-amber-900 mb-1">Temporary password for {resetResult.email}</h4>
                <p className="text-xs text-amber-800 mb-2">Share this with the user immediately. It will not be shown again.</p>
                <code className="bg-white border border-amber-200 rounded px-3 py-1.5 text-sm font-mono text-amber-900 select-all">{resetResult.temp_password}</code>
              </div>
              <div className="flex gap-2">
                <button onClick={() => copy(resetResult.temp_password)} className="border border-amber-300 text-amber-800 rounded-md px-2 py-1 text-xs hover:bg-amber-100 flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copy
                </button>
                <button onClick={() => setResetResult(null)} className="text-amber-600 hover:text-amber-800"><X className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        )}

        {showCreate && (
          <form onSubmit={handleCreate} className="bg-white border border-[#E2E8E3] rounded-md p-5 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <input data-testid="new-user-email-input" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" />
            <input data-testid="new-user-name-input" required type="text" placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" />
            <input data-testid="new-user-password-input" required type="text" minLength={6} placeholder="Password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" />
            <div className="flex gap-2">
              <select data-testid="new-user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-sage-200 rounded-md px-3 py-2 text-sm flex-1 bg-white">
                <option value="consultant">Consultant</option>
                <option value="admin">Admin</option>
              </select>
              <button data-testid="submit-new-user-button" type="submit" className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700">Add</button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="text-center py-10 text-sage-500 text-sm">Loading users...</div>
        ) : err ? (
          <div className="text-center py-10 text-red-600 text-sm">{err}</div>
        ) : (
          <div className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-sage-50 text-xs uppercase tracking-wider text-sage-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">User</th>
                  <th className="text-left px-4 py-3 font-semibold">Role</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Created</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === user?.id || u.email === user?.email;
                  return (
                    <tr key={u.id} className="border-t border-sage-100 hover:bg-sage-50/40" data-testid={`user-row-${u.email}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-sage-900">{u.name || "—"} {isSelf && <span className="text-xs text-sage-400 font-normal">(you)</span>}</div>
                        <div className="text-xs text-sage-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          data-testid={`role-select-${u.email}`}
                          value={u.role}
                          disabled={isSelf}
                          onChange={(e) => handleRoleChange(u, e.target.value)}
                          className="border border-sage-200 rounded-md px-2 py-1 text-xs bg-white disabled:bg-sage-50 disabled:text-sage-400"
                        >
                          <option value="consultant">consultant</option>
                          <option value="admin">admin</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {u.is_active === false ? <Badge tone="red">Deactivated</Badge> : <Badge tone="green">Active</Badge>}
                      </td>
                      <td className="px-4 py-3 text-xs text-sage-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <button
                          data-testid={`toggle-active-${u.email}`}
                          onClick={() => handleToggleActive(u)}
                          disabled={isSelf}
                          title={u.is_active === false ? "Reactivate" : "Deactivate"}
                          className="border border-sage-200 text-sage-600 rounded-md px-2 py-1 text-xs hover:bg-sage-50 disabled:opacity-30"
                        >
                          {u.is_active === false ? <Check className="w-3 h-3 inline" /> : <X className="w-3 h-3 inline" />}
                        </button>
                        <button
                          data-testid={`reset-pw-${u.email}`}
                          onClick={() => handleResetPw(u)}
                          title="Reset password"
                          className="border border-sage-200 text-sage-600 rounded-md px-2 py-1 text-xs hover:bg-sage-50"
                        >
                          <Key className="w-3 h-3 inline" />
                        </button>
                        <button
                          data-testid={`delete-user-${u.email}`}
                          onClick={() => handleDelete(u)}
                          disabled={isSelf}
                          title="Delete user"
                          className="border border-red-200 text-red-600 rounded-md px-2 py-1 text-xs hover:bg-red-50 disabled:opacity-30"
                        >
                          <Trash2 className="w-3 h-3 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
